import { useEffect, useMemo, useState } from 'react';
import Button from 'components/Common/Button';
import VoiceCard from './components/VoiceCard';
import { Controller, Control, useWatch } from 'react-hook-form';
import { Box, Typography, useTheme } from '@mui/material';
import VoiceListingModal from './components/VoiceListingModal';
import { LibraryVoice } from './components/types';

type Props = {
  control: Control<any>;
  parent: string;
  voicesHook: any;
  voiceHook: any;
  languageAccentHook: any;
  prefilledId?: string | null;
  languages?: string | string[];
};

export default function VoiceLibrary({
  prefilledId,
  voiceHook,
  voicesHook,
  languageAccentHook,
  control,
  languages = [],
  parent
}: Props) {
  const theme = useTheme();
  const [open, setOpen] = useState(false);

  const { data: voiceData, isLoading: isVoiceLoading } = voiceHook(prefilledId);

  const CurrVoiceId = useWatch({
    control,
    name: `${parent}.voice_id` // you can pass a string or array of strings
  });

  const memorizedVoice = useMemo(() => {
    return voiceData?.data ?? {};
  }, [voiceData]);

  const [selectedVoice, setSelectedVoice] = useState<any>(
    memorizedVoice.voice_id && memorizedVoice.category !== 'cloned' ? memorizedVoice : {}
  );

  useEffect(() => {
    if (memorizedVoice?.voice_id && memorizedVoice?.category !== 'cloned') {
      setSelectedVoice(memorizedVoice);
    }
  }, [memorizedVoice]);

  return (
    <Controller
      control={control}
      name={parent}
      render={({ field: { value, onChange } }) => {
        return (
          <>
            {CurrVoiceId ? (
              <Box display='flex' alignItems='center' gap={2}>
                <Box flex={1}>
                  <VoiceCard voice={selectedVoice} setOpen={setOpen} isVoiceLoading={isVoiceLoading} />
                </Box>
              </Box>
            ) : (
              <Box
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  p: 2,
                  borderRadius: 1,
                  bgcolor: theme.palette.background.paper
                }}
              >
                <Typography variant='h6' color='text.primary'>
                  Voice Library
                </Typography>
                <Typography color='secondary' mb={1}>
                  Select a voice from voice library.
                </Typography>

                <Button variant='outlined' fullWidth sx={{ mt: '1rem' }} onClick={() => setOpen(true)}>
                  Select Voice
                </Button>
              </Box>
            )}
            <VoiceListingModal
              open={open}
              key={`${languages[0]}-${languages.length}`}
              voicesHook={voicesHook}
              languageAccentHook={languageAccentHook}
              selectedVoice={CurrVoiceId ? selectedVoice : {}}
              onClose={() => setOpen(false)}
              languages={typeof languages === 'string' ? [languages] : languages}
              onSelect={(voice: LibraryVoice) => {
                onChange({
                  ...value,
                  voice_id: voice.voice_id,
                  voice_public_owner_id: voice.public_owner_id,
                  voice_name: voice.name,
                  gender: voice.gender
                });
                setOpen(false);
                setSelectedVoice(voice);
              }}
            />
          </>
        );
      }}
    />
  );
}
